export type ExperimentalSettings = Record<string, boolean>

export const EXPERIMENTAL_DEFAULTS: ExperimentalSettings = {
  insights: false,
  playwright_tests: false,
  site_map: false,
}

export interface UserSettings {
  notify_on_project_completion: boolean
  experimental_settings: ExperimentalSettings
  /** TRUE for an account that has not been shown the welcome dialog yet — i.e. a brand-new signup. */
  welcome_pending: boolean
}

export interface UserSettingsPatch {
  notify_on_project_completion?: boolean
  experimental_settings?: Partial<ExperimentalSettings>
  /** Reports that the welcome dialog has been shown; write-once, never unset. */
  welcome_shown?: boolean
}

export interface ActiveSubscription {
  plan: { key: string; name: string }
  status: string
  /** Unix timestamp (seconds) when the current billing period ends — may be null */
  currentPeriodEnd: number | null
  cancelAtPeriodEnd: boolean
  seatCount: number
}

export interface SubscriptionResponse {
  subscription: ActiveSubscription | null
  workspaceId: string | null
}

export type UsageTaskKind = 'exploration' | 'journey' | 'task'

export interface ProjectUsageTask {
  task_id: string
  kind: UsageTaskKind
  label: string
  date: string
  credits: number
}

export interface ProjectUsage {
  project_id: string
  project_name: string
  total_credits: number
  refunded_credits?: number
  qa_costs_refunded?: boolean
  /**
   * Per-task rows, present only in the /api/usage?project_id= drill-down response. The team-level
   * /api/usage ships per-project aggregates without them — its readers (credit gate, CreditMeter,
   * team switcher) consume only the totals — which shrinks the ~674KB payload the old response
   * re-downloaded on every page load (QA bug bug-mtxawj2w-411z).
   */
  tasks?: ProjectUsageTask[]
}

/** One team's balance, as reported alongside a usage response so the UI can offer a switcher. */
export interface TeamCreditSummary {
  id: string
  name: string
  role: string
  is_owner: boolean
  member_count: number
  available_credits: number
  /** The paying plan's monthly grant, and the plan it comes from. Null plan = couldn't be resolved. */
  monthly_credits?: number
  plan_key?: string | null
  /**
   * Whether that plan includes inviting teammates (Pro and up). Server-computed so the teaser and the
   * gate agree, and because only the server can tell "no subscription" from "lookup failed".
   */
  can_invite?: boolean
  /** Who pays, so a teammate who can't change the plan knows who to ask. */
  owner_name?: string | null
  owner_email?: string | null
}

export interface UsageData {
  available_credits: number
  total_used: number
  projects: ProjectUsage[]
  usage_waived?: boolean
  usage_code?: string | null
  usage_code_ends_at?: string | null
  /**
   * Which team these numbers describe. Credits belong to a team, so a response without this is one
   * from a deployment (or a mock) that predates teams — treat it as the user's own account.
   */
  team_id?: string | null
  team_name?: string | null
  /** Every team the user belongs to, with its balance. Empty before teams exist for them. */
  teams?: TeamCreditSummary[]
}

export interface ApiTokenInfo {
  id: string
  user_id: string
  name: string
  created_at: string
}

export interface GeneratedApiToken {
  token: string
  id: string
  name: string
  created_at: string
}
